import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dice5 } from 'lucide-react';

import { Button } from '@/shared/ui/components/button';
import { Input } from '@/shared/ui/components/input';
import { Label } from '@/shared/ui/components/label';
import { Switch } from '@/shared/ui/components/switch';
import { useVenues } from '@/modules/venues/venues';
import type { DayOfWeek, DiscountCodeInput, DiscountScope, DiscountVenueScope } from './api';

const FIELD_CLASS = 'h-9 rounded-md border border-input bg-transparent px-3 text-sm shadow-xs';
const DAYS: DayOfWeek[] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

type ValueKind = 'PERCENTAGE' | 'FIXED';

export interface DiscountCodeDraft {
  code: string;
  scope: DiscountScope;
  valueKind: ValueKind;
  percentage: string;
  fixedAmount: string;
  fixedAmountCurrency: string;
  venueScope: DiscountVenueScope;
  venueId: string;
  discoverable: boolean;
  allowedSellableIds: string;
  minimumSpendAmount: string;
  startsAt: string;
  endsAt: string;
  validDaysOfWeek: DayOfWeek[];
  validFromTime: string;
  validUntilTime: string;
  redemptionCap: string;
}

export const emptyDiscountCodeDraft: DiscountCodeDraft = {
  code: '',
  scope: 'ITEM',
  valueKind: 'PERCENTAGE',
  percentage: '',
  fixedAmount: '',
  fixedAmountCurrency: '',
  venueScope: 'FRANCHISE',
  venueId: '',
  discoverable: false,
  allowedSellableIds: '',
  minimumSpendAmount: '',
  startsAt: '',
  endsAt: '',
  validDaysOfWeek: [],
  validFromTime: '',
  validUntilTime: '',
  redemptionCap: '',
};

function generateCode(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 8; i += 1) {
    code += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return code;
}

/** Converts a form draft into the wire request shape; empty optional fields become undefined. */
/** `<input type="datetime-local">` yields a floating "YYYY-MM-DDTHH:mm" with no offset;
 *  the browser's own local timezone is what the administrator meant, so `Date` parses it
 *  as local time and `toISOString` converts to the Instant the backend expects. */
function instantFromLocalInput(value: string): string | undefined {
  if (value.trim() === '') return undefined;
  return new Date(value).toISOString();
}

export function discountCodeDraftInput(draft: DiscountCodeDraft): DiscountCodeInput {
  const optional = (value: string): string | undefined => (value.trim() === '' ? undefined : value);
  return {
    code: draft.code.trim(),
    scope: draft.scope,
    percentageBasisPoints:
      draft.valueKind === 'PERCENTAGE' && draft.percentage.trim() !== ''
        ? Math.round(Number(draft.percentage) * 100)
        : undefined,
    fixedAmount: draft.valueKind === 'FIXED' ? optional(draft.fixedAmount) : undefined,
    fixedAmountCurrency:
      draft.valueKind === 'FIXED' ? optional(draft.fixedAmountCurrency) : undefined,
    venueScope: draft.venueScope,
    venueId: draft.venueScope === 'VENUE' ? optional(draft.venueId) : undefined,
    discoverable: draft.discoverable,
    allowedSellableIds: draft.allowedSellableIds
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id !== ''),
    minimumSpendAmount:
      draft.minimumSpendAmount.trim() === '' ? undefined : Number(draft.minimumSpendAmount),
    startsAt: instantFromLocalInput(draft.startsAt),
    endsAt: instantFromLocalInput(draft.endsAt),
    validDaysOfWeek: draft.validDaysOfWeek,
    validFromTime: optional(draft.validFromTime),
    validUntilTime: optional(draft.validUntilTime),
    redemptionCap: draft.redemptionCap.trim() === '' ? undefined : Number(draft.redemptionCap),
  };
}

/** Inverse of `instantFromLocalInput`: an Instant back to the local "YYYY-MM-DDTHH:mm"
 *  a datetime-local input expects, in the browser's own timezone. */
function localInputFromInstant(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function draftFromDiscountCode(discountCode: {
  code: string;
  scope: DiscountScope;
  percentageBasisPoints?: number;
  fixedAmount?: string;
  fixedAmountCurrency?: string;
  venueScope: DiscountVenueScope;
  venueId?: string;
  discoverable: boolean;
  allowedSellableIds: string[];
  minimumSpendAmount?: string;
  startsAt?: string;
  endsAt?: string;
  validDaysOfWeek: DayOfWeek[];
  validFromTime?: string;
  validUntilTime?: string;
  redemptionCap?: number;
}): DiscountCodeDraft {
  return {
    code: discountCode.code,
    scope: discountCode.scope,
    valueKind: discountCode.fixedAmount != null ? 'FIXED' : 'PERCENTAGE',
    percentage:
      discountCode.percentageBasisPoints != null
        ? String(discountCode.percentageBasisPoints / 100)
        : '',
    fixedAmount: discountCode.fixedAmount ?? '',
    fixedAmountCurrency: discountCode.fixedAmountCurrency ?? '',
    venueScope: discountCode.venueScope,
    venueId: discountCode.venueId ?? '',
    discoverable: discountCode.discoverable,
    allowedSellableIds: discountCode.allowedSellableIds.join(', '),
    minimumSpendAmount: discountCode.minimumSpendAmount ?? '',
    startsAt: localInputFromInstant(discountCode.startsAt),
    endsAt: localInputFromInstant(discountCode.endsAt),
    validDaysOfWeek: discountCode.validDaysOfWeek,
    validFromTime: discountCode.validFromTime ?? '',
    validUntilTime: discountCode.validUntilTime ?? '',
    redemptionCap: discountCode.redemptionCap != null ? String(discountCode.redemptionCap) : '',
  };
}

export function DiscountCodeFormFields({
  draft,
  onChange,
}: {
  draft: DiscountCodeDraft;
  onChange: (patch: Partial<DiscountCodeDraft>) => void;
}) {
  const { t } = useTranslation('common');
  const venuesQuery = useVenues();
  const venues = venuesQuery.data?.items ?? [];
  const [showDiscoverableNotice, setShowDiscoverableNotice] = useState(draft.discoverable);

  function toggleDay(day: DayOfWeek) {
    const next = draft.validDaysOfWeek.includes(day)
      ? draft.validDaysOfWeek.filter((candidate) => candidate !== day)
      : [...draft.validDaysOfWeek, day];
    onChange({ validDaysOfWeek: next });
  }

  return (
    <div className="grid gap-5">
      <fieldset className="grid gap-3 rounded-md border p-4">
        <legend className="px-1 text-sm font-medium">{t('discounts.form.identity')}</legend>

        <div className="grid gap-1">
          <Label htmlFor="discount-code">{t('discounts.form.code')}</Label>
          <div className="flex gap-2">
            <Input
              id="discount-code"
              value={draft.code}
              onChange={(event) => onChange({ code: event.target.value })}
              placeholder={t('discounts.form.codePlaceholder')}
              required
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => onChange({ code: generateCode() })}
            >
              <Dice5 className="size-4" aria-hidden="true" />
              {t('discounts.form.generate')}
            </Button>
          </div>
        </div>

        <div className="grid gap-1">
          <Label htmlFor="discount-scope">{t('discounts.form.scope')}</Label>
          <select
            id="discount-scope"
            value={draft.scope}
            onChange={(event) => onChange({ scope: event.target.value as DiscountScope })}
            className={FIELD_CLASS}
          >
            <option value="ITEM">{t('discounts.scope.ITEM')}</option>
            <option value="BILL">{t('discounts.scope.BILL')}</option>
          </select>
        </div>
      </fieldset>

      <fieldset className="grid gap-3 rounded-md border p-4">
        <legend className="px-1 text-sm font-medium">{t('discounts.form.value')}</legend>

        <div className="grid gap-1">
          <Label htmlFor="discount-value-kind">{t('discounts.form.valueKind')}</Label>
          <select
            id="discount-value-kind"
            value={draft.valueKind}
            onChange={(event) =>
              onChange({ valueKind: event.target.value as 'PERCENTAGE' | 'FIXED' })
            }
            className={FIELD_CLASS}
          >
            <option value="PERCENTAGE">{t('discounts.form.percentage')}</option>
            <option value="FIXED">{t('discounts.form.fixedAmount')}</option>
          </select>
        </div>

        {draft.valueKind === 'PERCENTAGE' ? (
          <div className="grid gap-1">
            <Label htmlFor="discount-percentage">{t('discounts.form.percentage')}</Label>
            <Input
              id="discount-percentage"
              inputMode="decimal"
              value={draft.percentage}
              onChange={(event) => onChange({ percentage: event.target.value })}
              placeholder="10"
              required
            />
          </div>
        ) : (
          <div className="flex gap-2">
            <div className="grid gap-1">
              <Label htmlFor="discount-fixed-amount">{t('discounts.form.fixedAmount')}</Label>
              <Input
                id="discount-fixed-amount"
                inputMode="decimal"
                value={draft.fixedAmount}
                onChange={(event) => onChange({ fixedAmount: event.target.value })}
                required
              />
            </div>
            <div className="grid gap-1">
              <Label htmlFor="discount-fixed-currency">{t('discounts.form.currency')}</Label>
              <Input
                id="discount-fixed-currency"
                value={draft.fixedAmountCurrency}
                onChange={(event) =>
                  onChange({ fixedAmountCurrency: event.target.value.toUpperCase() })
                }
                placeholder="EUR"
                className="w-24"
                required
              />
            </div>
          </div>
        )}
      </fieldset>

      <fieldset className="grid gap-3 rounded-md border p-4">
        <legend className="px-1 text-sm font-medium">{t('discounts.form.reach')}</legend>

        <div className="grid gap-1">
          <Label htmlFor="discount-venue-scope">{t('discounts.form.venueScope')}</Label>
          <select
            id="discount-venue-scope"
            value={draft.venueScope}
            onChange={(event) =>
              onChange({ venueScope: event.target.value as DiscountVenueScope, venueId: '' })
            }
            className={FIELD_CLASS}
          >
            <option value="FRANCHISE">{t('discounts.venueScope.FRANCHISE')}</option>
            <option value="VENUE">{t('discounts.venueScope.VENUE')}</option>
          </select>
        </div>

        {draft.venueScope === 'VENUE' && (
          <div className="grid gap-1">
            <Label htmlFor="discount-venue">{t('discounts.form.venue')}</Label>
            <select
              id="discount-venue"
              value={draft.venueId}
              onChange={(event) => onChange({ venueId: event.target.value })}
              className={FIELD_CLASS}
              required
            >
              <option value="" disabled>
                {t('discounts.form.venuePlaceholder')}
              </option>
              {venues.map((venue) => (
                <option key={venue.id} value={venue.id}>
                  {venue.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="grid gap-1">
          <Label htmlFor="discount-allowlist">{t('discounts.form.allowlist')}</Label>
          <Input
            id="discount-allowlist"
            value={draft.allowedSellableIds}
            onChange={(event) => onChange({ allowedSellableIds: event.target.value })}
            placeholder={t('discounts.form.allowlistPlaceholder')}
          />
          <p className="text-xs text-muted-foreground">{t('discounts.form.allowlistHint')}</p>
        </div>

        <div className="grid gap-1">
          <Label htmlFor="discount-min-spend">{t('discounts.form.minimumSpend')}</Label>
          <Input
            id="discount-min-spend"
            inputMode="decimal"
            value={draft.minimumSpendAmount}
            onChange={(event) => onChange({ minimumSpendAmount: event.target.value })}
            className="w-40"
          />
        </div>
      </fieldset>

      <fieldset className="grid gap-3 rounded-md border p-4">
        <legend className="px-1 text-sm font-medium">{t('discounts.form.validity')}</legend>

        <div className="flex gap-4">
          <div className="grid gap-1">
            <Label htmlFor="discount-starts-at">{t('discounts.form.startsAt')}</Label>
            <Input
              id="discount-starts-at"
              type="datetime-local"
              value={draft.startsAt}
              onChange={(event) => onChange({ startsAt: event.target.value })}
            />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="discount-ends-at">{t('discounts.form.endsAt')}</Label>
            <Input
              id="discount-ends-at"
              type="datetime-local"
              value={draft.endsAt}
              onChange={(event) => onChange({ endsAt: event.target.value })}
            />
          </div>
        </div>

        <div className="grid gap-1">
          <Label>{t('discounts.form.validDays')}</Label>
          <div className="flex flex-wrap gap-3">
            {DAYS.map((day) => (
              <label key={day} className="flex items-center gap-1 text-sm">
                <input
                  type="checkbox"
                  checked={draft.validDaysOfWeek.includes(day)}
                  onChange={() => toggleDay(day)}
                />
                {t(`discounts.days.${day}`)}
              </label>
            ))}
          </div>
        </div>

        <div className="flex gap-4">
          <div className="grid gap-1">
            <Label htmlFor="discount-from-time">{t('discounts.form.validFromTime')}</Label>
            <Input
              id="discount-from-time"
              type="time"
              value={draft.validFromTime}
              onChange={(event) => onChange({ validFromTime: event.target.value })}
            />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="discount-until-time">{t('discounts.form.validUntilTime')}</Label>
            <Input
              id="discount-until-time"
              type="time"
              value={draft.validUntilTime}
              onChange={(event) => onChange({ validUntilTime: event.target.value })}
            />
          </div>
        </div>
      </fieldset>

      <fieldset className="grid gap-3 rounded-md border p-4">
        <legend className="px-1 text-sm font-medium">{t('discounts.form.limits')}</legend>

        <div className="grid gap-1">
          <Label htmlFor="discount-redemption-cap">{t('discounts.form.redemptionCap')}</Label>
          <Input
            id="discount-redemption-cap"
            inputMode="numeric"
            value={draft.redemptionCap}
            onChange={(event) => onChange({ redemptionCap: event.target.value })}
            className="w-32"
          />
          <p className="text-xs text-muted-foreground">{t('discounts.form.redemptionCapHint')}</p>
        </div>

        <div className="grid gap-2">
          <div className="flex items-center gap-2">
            <Switch
              id="discount-discoverable"
              checked={draft.discoverable}
              onCheckedChange={(checked) => {
                onChange({ discoverable: checked });
                setShowDiscoverableNotice(checked);
              }}
            />
            <Label htmlFor="discount-discoverable">{t('discounts.form.discoverable')}</Label>
          </div>
          <p className="text-xs text-muted-foreground">
            {showDiscoverableNotice
              ? t('discounts.form.discoverableOnNotice')
              : t('discounts.form.discoverableOffNotice')}
          </p>
        </div>
      </fieldset>
    </div>
  );
}
